import React, { useEffect, useState } from 'react';
import { Invoice, Item, invoiceTotal } from '../types';
import { v4 as uuidv4 } from 'uuid';

type Props = {
  onCreate: (inv: Invoice) => void;
  onUpdate: (inv: Invoice) => void;
  editing: Invoice | null;
  onImport: (list: Invoice[]) => void;
  onCancel?: () => void;
};

function blankItem(): Item {
  return { id: uuidv4(), description: '', qty: 1, price: 0 };
}

export default function InvoiceForm({ onCreate, onUpdate, editing, onImport, onCancel }: Props) {
  const [customer, setCustomer] = useState('');
  const [date, setDate] = useState(() => new Date().toISOString().slice(0,10));
  const [items, setItems] = useState<Item[]>([blankItem()]);
  const [notes, setNotes] = useState('');

  useEffect(() => {
    if (editing) {
      setCustomer(editing.customer);
      setDate(editing.date);
      setItems(editing.items.length ? editing.items : [blankItem()]);
      setNotes(editing.notes || '');
    } else {
      setCustomer('');
      setDate(new Date().toISOString().slice(0,10));
      setItems([blankItem()]);
      setNotes('');
    }
  }, [editing]);

  function updateItem(id: string, patch: Partial<Item>) {
    setItems((s) => s.map((it) => it.id === id ? { ...it, ...patch } : it));
  }

  function addItem() {
    setItems((s) => [...s, blankItem()]);
  }

  function removeItem(id: string) {
    setItems((s) => s.filter((it) => it.id !== id));
  }

  function submit(e?: React.FormEvent) {
    e?.preventDefault();
    const inv: Invoice = {
      id: editing ? editing.id : uuidv4(),
      customer: customer || 'Unnamed',
      date,
      items: items.map(it => ({...it, qty: Number(it.qty), price: Number(it.price)})),
      notes,
    };
    if (editing) onUpdate(inv); else onCreate(inv);
    // reset when created
    if (!editing) {
      setCustomer('');
      setDate(new Date().toISOString().slice(0,10));
      setItems([blankItem()]);
      setNotes('');
    }
  }

  function handleImportFile(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files && e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      try {
        const parsed = JSON.parse(String(reader.result));
        if (Array.isArray(parsed)) onImport(parsed);
        else alert('Expected an array of invoices');
      } catch (err) {
        alert('Invalid JSON file');
      }
    };
    reader.readAsText(file);
  }

  return (
    <div className="panel">
      <h3>{editing ? 'Edit Invoice' : 'New Invoice'}</h3>
      <form onSubmit={submit}>
        <div className="form-row">
          <input value={customer} onChange={e=>setCustomer(e.target.value)} placeholder="Customer name" />
          <input type="date" value={date} onChange={e=>setDate(e.target.value)} />
        </div>

        <table className="items-table">
          <thead>
            <tr><th>Description</th><th style={{width:80}}>Qty</th><th style={{width:110}}>Price</th><th style={{width:60}}></th></tr>
          </thead>
          <tbody>
            {items.map(it => (
              <tr key={it.id}>
                <td><input value={it.description} onChange={e=>updateItem(it.id,{description:e.target.value})} placeholder="Item description" /></td>
                <td><input type="number" value={it.qty} onChange={e=>updateItem(it.id,{qty: Number(e.target.value) || 0})} min={0} /></td>
                <td><input type="number" value={it.price} onChange={e=>updateItem(it.id,{price: Number(e.target.value) || 0})} step="0.01" /></td>
                <td><button type="button" className="btn ghost" onClick={()=>removeItem(it.id)}>Remove</button></td>
              </tr>
            ))}
          </tbody>
        </table>

        <div style={{marginTop:8}}>
          <button type="button" className="btn ghost" onClick={addItem}>Add item</button>
        </div>

        <div style={{marginTop:12}} className="muted">Total: <span className="total">${invoiceTotal({id:'tmp',customer, date, items, notes}).toFixed(2)}</span></div>

        <div className="form-row" style={{marginTop:12}}>
          <button type="submit" className="btn">{editing ? 'Update' : 'Create'}</button>
          {editing && <button type="button" className="btn ghost" onClick={() => { if (typeof onCancel === 'function') onCancel(); }}>Cancel</button>}
          <label className="btn ghost" style={{cursor:'pointer'}}>
            Import
            <input className="import-input" type="file" accept="application/json" onChange={handleImportFile} />
          </label>
        </div>
      </form>
    </div>
  );
}
